// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import {
  DeleteButton,
  WatchlistToggleButton,
  CellData,
} from '@shared/ui-kit/components';
import { type ExpandedState, createColumnHelper } from '@tanstack/react-table';
import { LastUpdatedInfo } from '@advisor-app/components';
import { BuySellButton } from '@advisor-app/features/ui/table/components';
import { type Ticker } from '@shared/data-access/interfaces/redux/portfolio-state';
import { type WatchlistItem } from '@shared/data-access/interfaces/common/watchlist';
import { useConst } from '@shared/ui-kit/hooks/use-const';
import { type FC, useMemo } from 'react';
import { AssetBrand } from '@shared/ui-kit/components/popups';
import { useInvestmentPopup } from '@shared/tag-management/components/investment-popup';
import { useTranslation } from 'react-i18next';
import { Table } from '@shared/ui-kit/components/grouped-table';

export interface WatchlistTableProps {
  readonly data: Array<
    { name: string; subRows: WatchlistItem[] } & Partial<WatchlistItem>
  >;
  readonly onRemoveItem: (ticker: Ticker, tag: string) => void;
  readonly initialExpanded?: ExpandedState;
}

const columnHelper = createColumnHelper<WatchlistTableProps['data'][number]>();

export const WatchlistTable: FC<WatchlistTableProps> = ({
  data,
  onRemoveItem,
  initialExpanded,
}) => {
  const { t } = useTranslation();
  const { openInvestmentPopup } = useInvestmentPopup();

  const assetsClassMap = useConst(
    new Map(
      Object.entries({
        stock: t('glossary.stock', { ns: 'glossary' }),
        etf: t('glossary.etf', { ns: 'glossary' }),
        mutual_fund: t('glossary.mutual-fund', { ns: 'glossary' }),
        other: t('glossary.other', { ns: 'glossary' }),
      }),
    ),
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        cell: ({ getValue, row }) => (
          <div className="flex items-center gap-x-2 truncate">
            <Table.Expander expander={row} />
            {row.getCanExpand() ? (
              getValue()
            ) : (
              <AssetBrand
                ticker={row.original.ticker!}
                assetType={row.original.asset_type}
                label={row.original.name}
                logoUrl={row.original.url_logo}
                url={row.original.url_morningstar}
                analyzePopup={openInvestmentPopup}
                watchlistButton={
                  <WatchlistToggleButton
                    ticker={row.original.ticker!}
                    assetType={row.original.asset_type!}
                  />
                }
              />
            )}
          </div>
        ),
        header: t('common.name', { ns: 'common' }),
        enableHiding: false,
        maxSize: 300,
      }),

      columnHelper.accessor('ticker', {
        header: t('common.ticker-isin', { ns: 'common' }),
        enableHiding: false,
        cell: ({ getValue }) => getValue() ?? '',
      }),

      columnHelper.accessor('currency', {
        header: t('common.currency', { ns: 'common' }),
        cell: ({ getValue, row }) =>
          !row.getCanExpand() && <CellData format="text" value={getValue()} />,
      }),

      columnHelper.accessor('asset_type', {
        header: t('glossary.vehicles', { ns: 'glossary' }),
        cell: ({ getValue, row }) =>
          row.getCanExpand() ? '' : assetsClassMap.get(getValue()!),
      }),

      columnHelper.accessor('asset_class', {
        id: 'asset_class',
        header: t('glossary.asset-class', { ns: 'glossary' }),
        cell: ({ row, getValue }) =>
          !row.getCanExpand() && (
            <CellData format="text" value={getValue()} oneLine />
          ),
      }),

      columnHelper.accessor('ytd_return', {
        header: t('glossary.investment-returns-ytd', { ns: 'glossary' }),
        meta: {
          tooltip: 'common.ytdReturn',
          alignment: 'end',
        },
        cell: ({ getValue, row }) =>
          !row.getCanExpand() && (
            <CellData value={getValue()} format="performance" />
          ),
      }),

      columnHelper.accessor('y1_return', {
        header: t('glossary.investment-returns-1y', { ns: 'glossary' }),
        meta: {
          tooltip: 'common.oneYReturn',
          alignment: 'end',
        },
        cell: ({ getValue, row }) =>
          !row.getCanExpand() && (
            <CellData value={getValue()} format="performance" />
          ),
      }),

      columnHelper.accessor('y3_return', {
        header: t('glossary.investment-returns-3y', { ns: 'glossary' }),
        meta: {
          tooltip: 'common.threeYReturn',
          alignment: 'end',
        },
        cell: ({ getValue, row }) =>
          !row.getCanExpand() && (
            <CellData value={getValue()} format="performance" />
          ),
      }),

      columnHelper.accessor('y5_return', {
        header: t('glossary.investment-returns-5y', { ns: 'glossary' }),
        meta: {
          tooltip: 'common.fiveYReturn',
          alignment: 'end',
        },
        cell: ({ getValue, row }) =>
          !row.getCanExpand() && (
            <CellData value={getValue()} format="performance" />
          ),
      }),

      columnHelper.accessor('y3_vol', {
        header: t('glossary.3y-vol', { ns: 'glossary' }),
        meta: {
          tooltip: 'common.threeYVol',
          alignment: 'end',
        },
        cell: ({ getValue, row }) =>
          !row.getCanExpand() && (
            <Table.Data value={getValue()} format="percentage" emptyValue={-1} />
          ),
      }),

      columnHelper.accessor('max_draw_down', {
        header: t('glossary.max-drawdown', { ns: 'glossary' }),
        meta: {
          tooltip: 'kpis.maxDrawdown',
          alignment: 'end',
        },
        cell: ({ getValue, row }) =>
          !row.getCanExpand() && (
            <Table.Data value={getValue()} format="percentage" emptyValue={-1} />
          ),
      }),

      columnHelper.accessor('inception_return', {
        header: t('glossary.investment-returns-inception', { ns: 'glossary' }),
        meta: {
          initiallyHidden: true,
          alignment: 'end',
        },
        cell: ({ getValue, row }) =>
          !row.getCanExpand() && (
            <CellData value={getValue()} format="performance" />
          ),
      }),

      columnHelper.accessor('m1_return', {
        header: t('glossary.investment-returns-m1', { ns: 'glossary' }),
        meta: {
          initiallyHidden: true,
          alignment: 'end',
        },
        cell: ({ getValue, row }) =>
          !row.getCanExpand() && (
            <CellData value={getValue()} format="performance" />
          ),
      }),

      columnHelper.accessor('m3_return', {
        header: t('glossary.investment-returns-m3', { ns: 'glossary' }),
        meta: {
          initiallyHidden: true,
          alignment: 'end',
        },
        cell: ({ getValue, row }) =>
          !row.getCanExpand() && (
            <CellData value={getValue()} format="performance" />
          ),
      }),

      columnHelper.accessor('m6_return', {
        header: t('glossary.investment-returns-m6', { ns: 'glossary' }),
        meta: {
          initiallyHidden: true,
          alignment: 'end',
        },
        cell: ({ getValue, row }) =>
          !row.getCanExpand() && (
            <CellData value={getValue()} format="performance" />
          ),
      }),

      columnHelper.accessor('price', {
        header: t('common.price', { ns: 'common' }),
        meta: {
          initiallyHidden: true,
          alignment: 'end',
        },
        cell: ({ getValue, row }) =>
          !row.getCanExpand() && <CellData value={getValue()} format="currency" />,
      }),

      columnHelper.accessor('price_as_of', {
        header: t('research.price-as-of', { ns: 'research' }),
        meta: {
          initiallyHidden: true,
        },
        cell: ({ getValue, row }) =>
          !row.getCanExpand() && <CellData value={getValue()} format="date" />,
      }),

      columnHelper.display({
        id: 'options',
        enableSorting: false,
        enableHiding: false,
        minSize: 130,
        meta: { showColumnVisibilitySelector: true },
        cell: ({ row }) =>
          row.getCanExpand() ? (
            ''
          ) : (
            <div className="flex items-center justify-end !gap-4">
              <BuySellButton
                operation="buy"
                ticker={row.original.ticker ?? row.original.isin ?? ''}
                assetType={row.original.asset_type ?? 'stock'}
                dfiUrl={row.original.dfi_url}
              />
              <DeleteButton
                onClick={() =>
                  onRemoveItem(row.original.ticker!, row.original.tag!)
                }
              />
            </div>
          ),
      }),
    ],
    [assetsClassMap, onRemoveItem, openInvestmentPopup, t],
  );

  return (
    <div className="overflow-x-auto">
      <LastUpdatedInfo data={data} />
      <Table.Grouped
        data={data}
        columns={columns}
        initialExpanded={initialExpanded}
        state-id="selected-assets-table"
      />
    </div>
  );
};
